import { computed, inject } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { NbxDialogService } from '@nbx/components';
import { StoreResetRegistry, withReset, withResource, withSaver } from '@nbx/core/state';
import { LocalStorageService } from '@nbx/core/storage';

import { MessageService } from 'primeng/api';

import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';

import { APP_PATHS_SLASH } from '@app/core/enums/paths/app.paths';
import { AuthResponse, AuthTokenRequest } from '@interfaces/auth.interface';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { AuthService } from '@services/auth.service';

export interface AuthCredentials {
  email: string;
  password: string;
  remember: boolean;
}

interface AuthStateModel {
  accessToken: string;
  refreshToken: string;
  sessionId: string;
  hostName: string | null;
  hostCodeName: string | null;
}

interface StoredRedirect {
  url: string;
  hostCodeName: string | null;
}

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
/** Where the signed-in host's code name is kept; the rental wizard stamps its draft with it. */
export const HOST_CODE_NAME_KEY = 'host_code_name';
const EMAIL_KEY = 'email';
const PASSWORD_KEY = 'password';
const REDIRECT_URL_KEY = 'redirectUrl';
const SESSION_ID_KEY = 'session_id';
/** The keys another tab writes when it signs in, refreshes or signs out. */
const SHARED_SESSION_KEYS: readonly (string | null)[] = [
  null,
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  SESSION_ID_KEY,
  HOST_CODE_NAME_KEY,
];

function readStorageValue(key: string): string {
  return localStorage.getItem(key) ?? '';
}

function persistTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

function clearStoredTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(SESSION_ID_KEY);
}

/** The session id that outlives a reload, or a fresh one when a token exists without it. */
function readSessionId(): string {
  if (!readStorageValue(ACCESS_TOKEN_KEY)) {
    return '';
  }
  const stored = readStorageValue(SESSION_ID_KEY);
  if (stored) {
    return stored;
  }
  const created = crypto.randomUUID();
  localStorage.setItem(SESSION_ID_KEY, created);
  return created;
}

/** Keeps the URL to come back to, tied to the host it belongs to. */
function storeRedirect(url: string, hostCodeName: string | null): void {
  const entry: StoredRedirect = { url, hostCodeName };
  localStorage.setItem(REDIRECT_URL_KEY, JSON.stringify(entry));
}

/**
 * Takes the stored redirect once: null when there is none or when it belongs to another host,
 * so a login into a different host never lands on the previous host's page.
 */
function consumeStoredRedirect(currentHost: string | null): string | null {
  const entry = readStoredRedirect();
  localStorage.removeItem(REDIRECT_URL_KEY);
  if (!entry) {
    return null;
  }
  return entry.hostCodeName === null || entry.hostCodeName === currentHost ? entry.url : null;
}

/** The redirect stored by a guard or a forced logout; a bare string is an older build's entry. */
function readStoredRedirect(): StoredRedirect | null {
  const raw = localStorage.getItem(REDIRECT_URL_KEY);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<StoredRedirect>;
    return typeof parsed.url === 'string'
      ? { url: parsed.url, hostCodeName: parsed.hostCodeName ?? null }
      : null;
  } catch {
    return { url: raw, hostCodeName: null };
  }
}

/**
 * True when a JWT is missing, unparseable, or within a 30-second pre-expiry buffer,
 * so guards proactively refresh before the server rejects the token.
 */
function isJwtExpired(token: string): boolean {
  if (!token) {
    return true;
  }
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return true;
    }
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded)) as { exp?: number };
    if (!payload.exp) {
      return false;
    }
    const bufferMs = 30_000;
    return Date.now() >= payload.exp * 1000 - bufferMs;
  } catch {
    return true;
  }
}

/**
 * What a refresh came to: new tokens (`refreshed`), the session cannot continue (`failed`), or
 * the session it was started for has ended or been replaced since (`stale`) — a stale answer is
 * not about the current session, so callers neither use it nor end the session over it.
 */
export type RefreshOutcome = 'refreshed' | 'failed' | 'stale';

interface RefreshTracker {
  inFlight: Observable<RefreshOutcome> | null;
  generation: number;
}

function createRefreshTracker(): RefreshTracker {
  return { inFlight: null, generation: 0 };
}

/** Disowns the refresh in flight: a later answer is stale, and the next refresh starts afresh. */
function disownRefresh(refresh: RefreshTracker): void {
  refresh.generation++;
  refresh.inFlight = null;
}

/**
 * Auth/session store: tokens, host identity and the session id seed synchronously from
 * localStorage (SPA, no SSR hazard). `session` keys every tenant-scoped resource (`SESSION_KEY`):
 * a new id per login, none while logged out. `host` is a resource gated on `accessToken`;
 * `login`/`validateHost` are savers. `refresh()` stays an Observable for the interceptor and
 * guards to compose directly (convention-10 exception) and shares one request in flight. `host`
 * is keyed on the session, not the token, so a refresh does not refetch it. Other tabs share the
 * tokens through localStorage; their `storage` events end or update the session here.
 */
export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState<AuthStateModel>(() => ({
    accessToken: readStorageValue(ACCESS_TOKEN_KEY),
    refreshToken: readStorageValue(REFRESH_TOKEN_KEY),
    sessionId: readSessionId(),
    hostName: null,
    hostCodeName: localStorage.getItem(HOST_CODE_NAME_KEY),
  })),
  withProps(() => ({
    _authService: inject(AuthService),
    _router: inject(Router),
    _localStorage: inject(LocalStorageService),
    _resetRegistry: inject(StoreResetRegistry),
    _dialogs: inject(NbxDialogService),
    _messages: inject(MessageService, { optional: true }),
    /** `generation` moves whenever the session ends or changes, so an older refresh cannot land. */
    _refresh: createRefreshTracker(),
  })),
  withResource(
    'host',
    store =>
      rxResource({
        params: () => store.sessionId() || undefined,
        stream: () => store._authService.getHost(),
      }),
    { fallback: null }
  ),
  withComputed(store => ({
    session: computed(() => store.sessionId() || undefined),
    currentUserRole: computed(() => store.host()?.role ?? null),
  })),
  withSaver(
    'login',
    store => (req: AuthTokenRequest & { remember: boolean }) => store._authService.login(req),
    {
      invalidates: ['host'],
      onSuccess: (store, response, req) => {
        const sessionId = crypto.randomUUID();
        disownRefresh(store._refresh);
        patchState(store, {
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          sessionId,
        });
        persistTokens(response.accessToken, response.refreshToken);
        localStorage.setItem(SESSION_ID_KEY, sessionId);
        store._localStorage.setItem(EMAIL_KEY, req.remember ? req.username : '');
        store._localStorage.removeItem(PASSWORD_KEY);
        const currentHost = store.hostCodeName() ?? localStorage.getItem(HOST_CODE_NAME_KEY);
        void store._router.navigateByUrl(
          consumeStoredRedirect(currentHost) ?? APP_PATHS_SLASH.HOME
        );
      },
    }
  ),
  withSaver(
    'validateHost',
    store => (externalCodeName: string) => store._authService.validateHost(externalCodeName),
    {
      onSuccess: (store, response, externalCodeName) => {
        patchState(store, { hostName: response.name, hostCodeName: externalCodeName });
        localStorage.setItem(HOST_CODE_NAME_KEY, externalCodeName);
      },
    }
  ),
  withMethods(store => {
    /** Re-reads localStorage so external token removal (e.g. DevTools) is picked up. */
    function syncTokensFromStorage(): void {
      const storedAccess = readStorageValue(ACCESS_TOKEN_KEY);
      const storedRefresh = readStorageValue(REFRESH_TOKEN_KEY);
      if (storedAccess !== store.accessToken() || storedRefresh !== store.refreshToken()) {
        patchState(store, { accessToken: storedAccess, refreshToken: storedRefresh });
      }
    }

    /**
     * True when another tab rotated the refresh token while this tab's refresh with the previous
     * one was refused: this tab takes the rotated tokens instead of ending a live session. Tokens
     * of another session or host (another tab signed out and in again) are never taken.
     */
    function adoptTokensRotatedElsewhere(
      sentRefreshToken: string,
      sentSessionId: string,
      sentHostCodeName: string | null
    ): boolean {
      const storedRefresh = readStorageValue(REFRESH_TOKEN_KEY);
      if (!storedRefresh || storedRefresh === sentRefreshToken) {
        return false;
      }
      if (
        readStorageValue(SESSION_ID_KEY) !== sentSessionId ||
        localStorage.getItem(HOST_CODE_NAME_KEY) !== sentHostCodeName
      ) {
        return false;
      }
      syncTokensFromStorage();
      return !!store.accessToken();
    }

    /**
     * Ends the session: the session key goes first so every tenant-scoped resource idles, then
     * every open dialog and toast closes (a toast of the ended session would otherwise still show
     * after the next sign-in, possibly as another host), then the registry restores each store's
     * state, then the tokens leave storage and the visitor lands on the sign-in page. The sign-in
     * page itself is never kept as the page to come back to. A refresh still in flight is disowned
     * so its answer cannot sign the visitor back in.
     */
    function logout(redirectUrl?: string): void {
      disownRefresh(store._refresh);
      const hostCodeName = store.hostCodeName() ?? localStorage.getItem(HOST_CODE_NAME_KEY);
      if (redirectUrl && !redirectUrl.startsWith(APP_PATHS_SLASH.SIGN_IN)) {
        storeRedirect(redirectUrl, hostCodeName);
      }
      patchState(store, {
        accessToken: '',
        refreshToken: '',
        sessionId: '',
        hostName: null,
      });
      store._dialogs.closeAll();
      store._messages?.clear();
      store._resetRegistry.resetAll();
      clearStoredTokens();
      store._router.navigate([APP_PATHS_SLASH.SIGN_IN]);
    }

    return {
      logout,
      isAuthenticated(): boolean {
        syncTokensFromStorage();
        return !!store.accessToken();
      },
      hasRefreshToken(): boolean {
        syncTokensFromStorage();
        return !!store.refreshToken();
      },
      /** Checks the stored access token against the current time on every call. */
      isAccessTokenExpired(): boolean {
        syncTokensFromStorage();
        return isJwtExpired(store.accessToken());
      },
      /** Keeps the URL a guard wants to come back to after the next login into this host. */
      rememberRedirect(url: string): void {
        storeRedirect(url, store.hostCodeName() ?? localStorage.getItem(HOST_CODE_NAME_KEY));
      },
      /**
       * Composable refresh kept as an Observable for the interceptor/guards to pipe. One request
       * is shared while it is in flight, so a guard and a burst of 401s never rotate the refresh
       * token twice. An answer arriving after the session it was started for ended or was replaced
       * (here, or in another tab whose change this tab has not followed yet) is ignored as `stale`.
       */
      refresh(): Observable<RefreshOutcome> {
        if (store._refresh.inFlight) {
          return store._refresh.inFlight;
        }
        const refreshToken = store.refreshToken();
        if (!refreshToken) {
          return of('failed');
        }
        const generation = store._refresh.generation;
        const sessionId = store.sessionId();
        const hostCodeName = localStorage.getItem(HOST_CODE_NAME_KEY);
        const isCurrent = (): boolean =>
          store._refresh.generation === generation &&
          store.sessionId() === sessionId &&
          readStorageValue(SESSION_ID_KEY) === sessionId &&
          localStorage.getItem(HOST_CODE_NAME_KEY) === hostCodeName;
        const request: Observable<RefreshOutcome> = store._authService
          .refreshToken(refreshToken)
          .pipe(
            map((response: AuthResponse): RefreshOutcome => {
              if (!isCurrent()) {
                return 'stale';
              }
              patchState(store, {
                accessToken: response.accessToken,
                refreshToken: response.refreshToken,
              });
              persistTokens(response.accessToken, response.refreshToken);
              return 'refreshed';
            }),
            catchError(() => {
              if (!isCurrent()) {
                return of<RefreshOutcome>('stale');
              }
              return of<RefreshOutcome>(
                adoptTokensRotatedElsewhere(refreshToken, sessionId, hostCodeName)
                  ? 'refreshed'
                  : 'failed'
              );
            }),
            finalize(() => {
              if (store._refresh.inFlight === request) {
                store._refresh.inFlight = null;
              }
            }),
            shareReplay({ bufferSize: 1, refCount: false })
          );
        store._refresh.inFlight = request;
        return request;
      },
      /**
       * Fills the login form from the last "remember me". Only the address comes back: the password
       * is never stored, and any left by an earlier build is deleted on the way past. Staying signed
       * in is the refresh token's job, and it does not need the password to do it.
       */
      loadLoginCredentials(): AuthCredentials {
        const email = store._localStorage.getItem(EMAIL_KEY) ?? '';
        store._localStorage.removeItem(PASSWORD_KEY);
        return { email, password: '', remember: !!email };
      },
      /**
       * Follows the session as other tabs change it: a logout there ends it here too, so no tab
       * keeps showing the host's data; a refresh there hands this tab the rotated tokens; a
       * sign-in there gives this tab its session and host, and disowns this tab's refresh for the
       * previous one. A logout there was chosen by the user, so, as in the tab where it was
       * clicked, no page to come back to is kept.
       */
      followOtherTabs(event: StorageEvent): void {
        if (!SHARED_SESSION_KEYS.includes(event.key)) {
          return;
        }
        if (store.accessToken() && !readStorageValue(ACCESS_TOKEN_KEY)) {
          logout();
          return;
        }
        syncTokensFromStorage();
        const sessionId = readStorageValue(SESSION_ID_KEY);
        if (store.accessToken() && sessionId && sessionId !== store.sessionId()) {
          disownRefresh(store._refresh);
          patchState(store, { sessionId, hostCodeName: localStorage.getItem(HOST_CODE_NAME_KEY) });
        }
      },
    };
  }),
  withReset({ global: false }),
  withHooks(store => {
    const onStorage = (event: StorageEvent): void => store.followOtherTabs(event);
    return {
      onInit: () => window.addEventListener('storage', onStorage),
      onDestroy: () => window.removeEventListener('storage', onStorage),
    };
  })
);
